// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { productListFilterAccordionSectionStyles } from "@insite/content-library/Widgets/ProductList/ProductListFilterAccordionSection";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => {
    // Xng-Change
    // store getProductListDataView(state) into productData
    const productData = getProductListDataView(state);
    return {
        loaded: !!productData.value,
        stockedItemsOnly: state.pages.productList.productFilters.stockedItemsOnly,
        displayFacetsForStockedItems: getSettingsCollection(state).productSettings.displayFacetsForStockedItems,
        // Xng-Change
        stockedItemsCount: parseInt((productData as any).properties.stockedItemsCount, 10) || 0,
    }
};

const mapDispatchToProps = {
    addProductFilters,
    removeProductFilters,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ProductListStockedItemsFilterStyles {
    // Xng-Change
    facetCount?: TypographyPresentationProps;
}

export const productListStockedItemsFilterStyles: ProductListStockedItemsFilterStyles = {};

const styles = productListStockedItemsFilterStyles;

const ProductListStockedItemsFilter: FC<Props> = ({
    stockedItemsOnly,
    loaded,
    addProductFilters,
    removeProductFilters,
    displayFacetsForStockedItems,
    // Xng-Change
    stockedItemsCount,
}) => {
    // Xng-Change add stockedItemsCount to the condition
    if (!loaded || !displayFacetsForStockedItems || !stockedItemsCount) {
        return null;
    }

    const onChangeFacet = (facet: FacetModel) => {
        if (facet.selected) {
            removeProductFilters({ stockedItemsOnly: false });
        } else {
            addProductFilters({ stockedItemsOnly: true });
        }
    };

    // Xng-Change parse stockedItemsCount into facet count
    const facet = [
        {
            id: "stocked",
            // XNG-Change:change translation
            name: translate("Stocking Items"),
            count: stockedItemsCount,
            selected: stockedItemsOnly ?? false,
        },
    ];

    // XNG-Changes: change structure
    return (
        <>
            {facet?.map(facet => (
                <CheckboxGroup key={facet.id} {...productListFilterAccordionSectionStyles.checkBoxGroup}>
                    <Checkbox
                        {...(facet.selected
                            ? productListFilterAccordionSectionStyles.checkBoxSelected
                            : productListFilterAccordionSectionStyles.checkBox)}
                        checked={facet.selected}
                        onChange={() => onChangeFacet(facet)}
                        data-test-selector={`facetFilter${facet.id}`}
                    >
                        {facet.name}
                        {facet.count && (
                            <Typography {...styles.facetCount}>{`(${facet.count})`}</Typography>
                        )}
                    </Checkbox>
                </CheckboxGroup>
            ))}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListStockedItemsFilter),
    definition: {
        group: "Product List",
        displayName: "Stocked Items Filter",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
